<template>
  <table class="table">
    <thead>
      <th class="head1">
        Round
      </th>
      <th class="head">
        <NumeraiTooltip text="When the submissions are due.">
          Close
        </NumeraiTooltip>
      </th>
      <th class="head">
        <NumeraiTooltip text="When payouts finalize.">
          Resolve
        </NumeraiTooltip>
      </th>
      <th v-if="isAppSignals()" class="head">
        <NumeraiTooltip text="Maximum churn over past week."
          >Churn</NumeraiTooltip
        >
      </th>
      <th class="head">
        <NumeraiTooltip :text="payoutFormulaTooltip">
          Payout
        </NumeraiTooltip>
      </th>
      <th class="head">
        <NumeraiTooltip text="Amount of stake at risk in this round.">
          At-risk
        </NumeraiTooltip>
      </th>
      <th class="head">
        <NumeraiTooltip text="Payout Factor">PF</NumeraiTooltip>
      </th>
      <th class="head">Multipliers</th>
      <th
        v-for="(obj, scoreKey) in payoutScoresConfig"
        :key="scoreKey"
        class="head"
        :class="{ [scoreKey]: true }"
      >
        <NumeraiTooltip :text="obj.scoreTooltip">
          {{ obj.displayName }}
        </NumeraiTooltip>
      </th>
      <th
        v-for="(obj, scoreKey) in auxiliaryScoresConfig"
        :key="scoreKey"
        class="head"
        :class="{ [scoreKey]: true }"
      >
        <NumeraiTooltip :text="obj.scoreTooltip">
          {{ obj.displayName }}
        </NumeraiTooltip>
      </th>
    </thead>
    <tbody>
      <tr
        v-for="entry in submissionEntries"
        :key="entry.roundNumber"
        class="resolved"
        @click="open(entry)"
        @click.middle="openInNewTab(entry)"
      >
        <td class="data1">
          {{ entry.roundNumber }}
        </td>
        <td class="data">
          {{ formatDate(entry.roundCloseStakingTime) }}
        </td>
        <td class="data">
          {{ formatDate(entry.roundResolveTime) }}
        </td>
        <td v-if="isAppSignals()" class="data">
          <!-- eslint-disable -->
          <NumeraiTooltip
            v-if="entry.prevWeekChurnMax !== null"
            :text="churnTooltip(entry.churnThreshold)"
            >
            <span
            :class="{
              'churn-breached': entry.churnThreshold && (entry.prevWeekChurnMax > entry.churnThreshold),
            }"
            >
            {{ formatScore(entry.prevWeekChurnMax) }}
          </span>
          </NumeraiTooltip>
          <!-- eslint-enable -->
          <span v-else class="null-number"></span>
        </td>
        <td class="data">
          <NumeraiCurrency sign :value="entry.payoutPending" />
        </td>
        <td class="data">
          <NumeraiCurrency
            v-if="parseFloat(entry.atRisk) > 0"
            :value="entry.atRisk"
            class="stake"
          />
          <span v-else class="null-number"> </span>
        </td>
        <td class="data">
          <NumeraiTooltip text="Determined by total stake.">
            {{ formatScore(entry.roundPayoutFactor) }}
          </NumeraiTooltip>
        </td>
        <td class="data">
          <div v-if="entry.atRisk && entry.atRisk > 0">
            {{
              formatStakeType(
                entry.corrMultiplier,
                entry.mmcMultiplier,
                entry.tcMultiplier,
                entry.roundNumber,
              )
            }}
          </div>
          <div v-else class="null-number"></div>
        </td>
        <td
          v-for="(obj, scoreKey) in payoutScoresConfig"
          :key="scoreKey"
          class="data"
          :class="{
            'null-number': entry[scoreKey] === null,
            [scoreKey]: true,
          }"
        >
          <NumeraiTooltip
            :text="
              'Percentile: ' + formatPercentile(entry[scoreKey + 'Percentile'])
            "
          >
            <span>{{ formatScore(entry[scoreKey]) }}</span>
          </NumeraiTooltip>
        </td>
        <td
          v-for="(obj, scoreKey) in auxiliaryScoresConfig"
          :key="scoreKey"
          class="data"
          :class="{
            'null-number': !entry[scoreKey],
            [scoreKey]: true,
          }"
        >
          <NumeraiTooltip
            :text="
              'Percentile: ' + formatPercentile(entry[scoreKey + 'Percentile'])
            "
          >
            <span>{{ formatScore(entry[scoreKey]) }}</span>
          </NumeraiTooltip>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import NumeraiTooltip from '../shared/NumeraiTooltip'
import NumeraiCurrency from '@/components/shared/NumeraiCurrency'

import {
  formatScore,
  formatCurrency,
  formatPercentile,
  formatStakeType,
  getTournamentNumber,
  getTournamentScoresConfig,
  isAppSignals,
} from '@/lib/utils'
import moment from 'moment'

export default {
  components: {
    NumeraiTooltip,
    NumeraiCurrency,
  },
  props: {
    submissionEntries: {
      type: Array,
      default: function() {
        return []
      },
    },
    username: { type: String, default: null },
  },
  computed: {
    payoutScoresConfig() {
      return getTournamentScoresConfig(getTournamentNumber(), 'payout')
    },
    payoutFormulaTooltip() {
      return `= AT-RISK * clip(±0.05, PF * ( MULTIPLIERS ))`
    },
    auxiliaryScoresConfig() {
      let auxiliaryScores = getTournamentScoresConfig(
        getTournamentNumber(),
        'showProfileTables',
      )
      // delete payout scores keys from auxiliary scores
      Object.keys(this.payoutScoresConfig).forEach(
        payoutScoreKey => delete auxiliaryScores[payoutScoreKey],
      )
      return auxiliaryScores
    },
  },
  methods: {
    isAppSignals,
    formatStakeType,
    formatScore,
    formatCurrency,
    formatPercentile,
    formatDate(x) {
      return moment(x)
        .utc()
        .format('MMM DD YYYY')
    },
    churnTooltip(roundChurnThreshold) {
      if (!roundChurnThreshold) {
        return 'Prevents payouts if too high.'
      }
      return 'At-risk set to 0 if > ' + roundChurnThreshold
    },
    open(entry) {
      // Route to the submissions page for the clicked entry
      this.$router.push(
        '/' + this.username + '/submissions/' + entry.roundNumber,
      )
    },
    openInNewTab(entry) {
      // Open the submissions page for the clicked entry in a new tab and switch to it
      const url = this.$router.resolve(
        '/' + this.username + '/submissions/' + entry.roundNumber,
      )
      window.open(url.href, '_blank', 'noopener,noreferrer')
    },
  },
}
</script>

<style>
.table {
  width: 100%;
  border-collapse: collapse;
}

th {
  padding: 9px;
  padding-bottom: 10px;
}
th.head1 {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  font-family: var(--brand-font);
  color: var(--color-primary);
  text-align: left;
  white-space: nowrap;
}
th.head {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  text-align: end;
  font-family: var(--brand-font);
  white-space: nowrap;
}

td {
  font-size: 12px;
  text-decoration: none;
  padding: 12px;
  border-top: 1px solid var(--paper-border-color) !important;
}

td.data {
  text-align: end;
  font-family: var(--number-font);
  font-weight: 500;
  white-space: nowrap;
}

td.data1 {
  text-align: left;
  font-family: var(--number-font);
  font-weight: 500;
  white-space: nowrap;
}

tr:hover {
  background-color: var(--paper-background-very-subtle);
  cursor: pointer;
}

tr:hover td {
  color: var(--color-primary);
}

tr.unresolved {
  color: var(--color-secondary);
}

tr.resolved td {
  /*font-weight: bold;*/
  font-weight: 500;
}

.churn-breached {
  color: var(--color-red);
}

@media screen and (max-width: 1390px) {
  .table-container {
    overflow-x: scroll;
  }
}
</style>
